import React, { createContext, useContext, useEffect, useState } from 'react';
import { useApi } from '../../classes/appApi';
import { getApiCacheRequests } from '../../service-worker/service-worker-utils';
import { useAuthContext } from './AppAuth';

interface CacheSnapshot {
    [path: string]: boolean;
}

interface OfflineContextType {
    isOffline: boolean;
    isFileCached: (urlOrPath: string) => boolean;
    cacheSnapshot: CacheSnapshot;
}

const OfflineContext = createContext<OfflineContextType>({
    isOffline: false,
    isFileCached: () => false,
    cacheSnapshot: {}
});

export const useOffline = () => {
    const context = useContext(OfflineContext);
    if (!context) {
        throw new Error('useOffline must be used within an OfflineProvider');
    }
    return context;
};

interface OfflineProviderProps {
    children: React.ReactNode;
}

export const OfflineProvider: React.FC<OfflineProviderProps> = ({ children }) => {
    const [isOffline, setIsOffline] = useState(false);
    const [cacheSnapshot, setCacheSnapshot] = useState<CacheSnapshot>({});
    const auth = useAuthContext();
    const api = useApi();

    const updateCacheSnapshot = async () => {
        try {
            if (!('caches' in window)) {
                return;
            }

            const requests = await getApiCacheRequests(auth.token);
            const snapshot: CacheSnapshot = {};

            requests.forEach((request: Request) => {
                // Extract just the path from the URL
                let path = new URL(request.url).pathname;
                // Strip the base path of the API
                let basePath = new URL(api.baseUrl).pathname;
                path = path.replace(basePath, '');
                snapshot[path] = true;
            });

            setCacheSnapshot(snapshot);
        } catch (error) {
            console.error('Error updating cache snapshot:', error);
        }
    };

    const isFileCached = (urlOrPath: string): boolean => {
        if (isOffline) {
            // If it's already a path (starts with /), use it directly
            // Otherwise, extract the path from the URL
            const path = urlOrPath.startsWith('/')
                ? urlOrPath
                : new URL(urlOrPath).pathname;
            return !!cacheSnapshot[path];
        }

        // When online, we don't need to check the cache
        return true;
    };

    useEffect(() => {
        const handleOnline = () => {
            // Clear snapshot when coming back online
            setCacheSnapshot({});
            setIsOffline(false);
        };

        const handleOffline = async () => {
            // Take snapshot of cache when going offline
            await updateCacheSnapshot();
            setIsOffline(true)
        };

        window.addEventListener('online', handleOnline);
        window.addEventListener('offline', handleOffline);

        // Initial snapshot if starting offline
        if (!navigator.onLine) {
            updateCacheSnapshot().then(() => {
                setIsOffline(true);
            });
        }

        return () => {
            window.removeEventListener('online', handleOnline);
            window.removeEventListener('offline', handleOffline);
        };
    }, [auth.token]);

    return (
        <OfflineContext.Provider value={{
            isOffline,
            isFileCached,
            cacheSnapshot
        }}>
            {children}
        </OfflineContext.Provider>
    );
}; 